import { Container, Graphics } from 'pixi.js';
import { COLLISION_CONFIG, type DamageResult } from '@astra/shared';
import { CollidableEntity } from './CollidableEntity';
import { tween, delay, cancelDelay } from '../../core/TweenManager';
import gsap from 'gsap';

/**
 * DamageableEntity - Has hull/shield and can take damage.
 *
 * Extends: CollidableEntity
 *
 * Provides:
 * - Hull and shield management
 * - takeDamage() - THE damage function (shield absorbs first, then hull)
 * - Stun system (3 second stun on collision)
 * - Default health bar UI (appears when damaged)
 * - Hooks for custom effects: onHit(), onDestroy()
 *
 * Examples: Player, Bot, Asteroid
 */
export abstract class DamageableEntity extends CollidableEntity {
  // Health
  hull: number = 100;
  maxHull: number = 100;
  shield: number = 0;
  maxShield: number = 0;

  // Stun state
  isStunned: boolean = false;
  stunEndTime: number = 0;

  // Destruction state
  protected isDestroyed: boolean = false;

  // Health bar UI (created on first damage)
  protected healthBarContainer?: Container;
  protected healthBarBg?: Graphics;
  protected healthBarFill?: Graphics;
  protected healthBarHideDelay?: gsap.core.Tween;

  // Health bar config
  protected readonly healthBarWidth: number = 50;
  protected readonly healthBarHeight: number = 6;
  protected readonly healthBarOffsetY: number = -40; // Above entity

  // Stun visual effect
  protected stunIndicator?: Graphics;
  private stunPulseTime: number = 0;
  protected stunIndicatorRadius: number = 35; // Slightly larger than ship, updated per ship size

  /**
   * THE damage function - same logic for ALL damageable entities.
   * Shield absorbs damage first, then hull takes the rest.
   *
   * Can be overridden for special cases (e.g., Station returns no damage).
   *
   * @param amount - Amount of damage to apply
   * @returns Damage breakdown and destroyed flag
   */
  takeDamage(amount: number): DamageResult {
    let remainingDamage = amount;
    let shieldDamage = 0;
    let hullDamage = 0;

    // Shield absorbs first
    if (this.shield > 0) {
      shieldDamage = Math.min(this.shield, remainingDamage);
      this.shield -= shieldDamage;
      remainingDamage -= shieldDamage;
    }

    // Hull takes the rest
    if (remainingDamage > 0) {
      hullDamage = Math.min(this.hull, remainingDamage);
      this.hull -= hullDamage;
    }

    const destroyed = this.hull <= 0;

    // Show health bar
    this.showHealthBar();

    // Trigger hooks
    if (destroyed && !this.isDestroyed) {
      this.isDestroyed = true;
      this.onDestroy();
    }

    return {
      shieldDamage,
      hullDamage,
      shieldRemaining: this.shield,
      hullRemaining: this.hull,
      destroyed,
    };
  }

  /**
   * Check if entity is dead (hull <= 0).
   */
  isDead(): boolean {
    return this.hull <= 0 || this.isDestroyed;
  }

  /**
   * Heal hull (not above max).
   * @returns Amount actually healed
   */
  healHull(amount: number): number {
    const healed = Math.min(amount, this.maxHull - this.hull);
    this.hull += healed;
    return healed;
  }

  /**
   * Recharge shield (not above max).
   * @returns Amount actually recharged
   */
  rechargeShield(amount: number): number {
    const recharged = Math.min(amount, this.maxShield - this.shield);
    this.shield += recharged;
    return recharged;
  }

  /**
   * Apply stun (3 seconds of no input).
   * Stun timer does NOT reset on additional collisions.
   */
  applyStun(): void {
    if (!this.isStunned) {
      this.isStunned = true;
      this.stunEndTime = Date.now() + COLLISION_CONFIG.STUN_DURATION_MS;
    }
  }

  /**
   * Update stun state. Call this in update() every frame.
   */
  updateStunState(): void {
    if (this.isStunned && Date.now() >= this.stunEndTime) {
      this.isStunned = false;
      this.stunEndTime = 0;
      // Zero out velocity on stun end to prevent immediately re-colliding
      // Player must rebuild speed from scratch after recovering from stun
      this.velocityX = 0;
      this.velocityY = 0;
    }

    // Update stun visual
    this.updateStunVisual();
  }

  /**
   * Create the stun indicator graphics (pulsing circle outline).
   * Called once when first stunned.
   */
  protected createStunIndicator(): void {
    this.stunIndicator = new Graphics();
    this.stunIndicator.zIndex = 99;
    this.addChild(this.stunIndicator);
  }

  /**
   * Update the stun visual effect.
   * Shows a pulsing red/orange outline when stunned.
   */
  protected updateStunVisual(): void {
    if (this.isStunned) {
      // Create indicator on first stun
      if (!this.stunIndicator) {
        this.createStunIndicator();
      }

      if (!this.stunIndicator) return;

      // Pulse at 4Hz (4 cycles per second)
      this.stunPulseTime += 0.067; // ~60fps, 4Hz = 0.067 per frame
      const pulse = Math.sin(this.stunPulseTime * Math.PI * 2);

      // Alpha oscillates between 0.3 and 0.8
      const alpha = 0.55 + pulse * 0.25;

      // Color shifts between red and orange
      const color = pulse > 0 ? 0xff4444 : 0xff8800;

      // Redraw the circle
      this.stunIndicator.clear();
      this.stunIndicator.circle(0, 0, this.stunIndicatorRadius);
      this.stunIndicator.stroke({ color, alpha, width: 3 });

      this.stunIndicator.visible = true;

      // Counter-rotate to keep indicator orientation fixed
      this.stunIndicator.rotation = -this.rotation;
    } else {
      // Hide when not stunned
      if (this.stunIndicator) {
        this.stunIndicator.visible = false;
        this.stunPulseTime = 0;
      }
    }
  }

  /**
   * Check if entity can accept player input.
   * Returns false when stunned.
   */
  canAcceptInput(): boolean {
    return !this.isStunned;
  }

  /**
   * Override applyCollision to also apply stun.
   */
  override applyCollision(newVelocity: { x: number; y: number }): void {
    super.applyCollision(newVelocity);
    this.applyStun();
  }

  /**
   * Show the health bar UI. Called automatically on damage.
   * Override to customize or disable.
   */
  protected showHealthBar(): void {
    // Create health bar on first use
    if (!this.healthBarContainer) {
      this.createHealthBar();
    }

    if (!this.healthBarContainer || !this.healthBarFill) return;

    // Update health bar fill
    const healthPercent = Math.max(0, this.hull / this.maxHull);
    this.healthBarFill.clear();

    // Color based on health: green -> orange -> red
    let fillColor = 0x44ff44; // Green
    if (healthPercent < 0.5) {
      fillColor = 0xff6644; // Red
    } else if (healthPercent < 0.75) {
      fillColor = 0xffaa00; // Orange
    }

    this.healthBarFill.roundRect(0, 0, this.healthBarWidth * healthPercent, this.healthBarHeight, 2);
    this.healthBarFill.fill({ color: fillColor, alpha: 1 });

    // Show
    this.healthBarContainer.visible = true;
    this.healthBarContainer.alpha = 1;

    // Cancel existing hide timer
    if (this.healthBarHideDelay) {
      cancelDelay(this.healthBarHideDelay);
    }

    // Auto-hide after 2 seconds
    this.healthBarHideDelay = delay(2000, () => {
      if (this.healthBarContainer) {
        tween({
          targets: this.healthBarContainer,
          alpha: 0,
          duration: 300,
          onComplete: () => {
            if (this.healthBarContainer) this.healthBarContainer.visible = false;
          },
        });
      }
    });
  }

  /**
   * Create the health bar graphics.
   * Called once on first damage.
   */
  protected createHealthBar(): void {
    this.healthBarContainer = new Container();
    this.healthBarContainer.visible = false;
    this.healthBarContainer.zIndex = 100;

    // Background (dark)
    this.healthBarBg = new Graphics();
    this.healthBarBg.roundRect(-1, -1, this.healthBarWidth + 2, this.healthBarHeight + 2, 2);
    this.healthBarBg.fill({ color: 0x000000, alpha: 0.7 });
    this.healthBarContainer.addChild(this.healthBarBg);

    // Fill (colored based on health)
    this.healthBarFill = new Graphics();
    this.healthBarContainer.addChild(this.healthBarFill);

    // Center above entity
    this.healthBarContainer.pivot.set(this.healthBarWidth / 2, 0);

    // Add to this container
    this.addChild(this.healthBarContainer);

    // Position above entity (local coordinates)
    this.healthBarContainer.position.set(0, this.healthBarOffsetY);
  }

  /**
   * Update health bar position.
   * Call in update() if health bar shouldn't rotate with entity.
   */
  protected updateHealthBarPosition(): void {
    if (this.healthBarContainer) {
      // Counter-rotate to keep horizontal
      this.healthBarContainer.rotation = -this.rotation;
    }
  }

  /**
   * Hook called when entity is hit. Override for custom effects.
   * @param damage - Damage amount
   * @param hitAngle - Angle of impact (radians)
   * @param damageType - Type of damage source
   */
  protected onHit(damage: number, hitAngle: number, damageType: 'projectile' | 'collision' | 'hazard'): void {
    // Default: no visual effect. Override in subclass.
  }

  /**
   * Hook called when entity is destroyed (hull <= 0). Override for explosion.
   */
  protected onDestroy(): void {
    // Default: no effect. Override in subclass for explosion animation.
  }

  /**
   * Cleanup health bar and stun indicator on destroy.
   */
  override destroy(): void {
    if (this.healthBarHideDelay) {
      cancelDelay(this.healthBarHideDelay);
    }
    this.healthBarContainer?.destroy();
    this.stunIndicator?.destroy();
    super.destroy();
  }
}
